import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import useSeo, { SITE_NAME, SITE_ORIGIN } from "@/hooks/useSeo";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import ArticleCard from "@/components/feature/ArticleCard";
import { articles } from "@/mocks/articles";
import { rankBoards } from "@/mocks/rank";
import { formatHeat } from "@/utils/format";

export default function ArticlePage() {
  const { id = "" } = useParams();
  const article = articles.find((item) => item.id === id);

  const related = useMemo(() => {
    if (!article) return [];
    const picked = articles.filter((item) => article.related.includes(item.id));
    if (picked.length >= 2) return picked.slice(0, 3);
    return articles.filter((item) => item.id !== article.id && item.categorySlug === article.categorySlug).slice(0, 3);
  }, [article]);

  const hotList = rankBoards.realtime.slice(0, 5);

  const jsonLd = useMemo(() => {
    if (!article) return null;
    return {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: article.title,
      description: article.summary,
      image: article.cover,
      datePublished: article.publishedAt,
      author: { "@type": "Organization", name: article.author },
      publisher: { "@type": "Organization", name: SITE_NAME, url: SITE_ORIGIN },
      mainEntityOfPage: `${SITE_ORIGIN}/article/${article.id}`,
      articleSection: article.categoryName,
      keywords: article.tags.join(","),
      breadcrumb: {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: SITE_ORIGIN },
          { "@type": "ListItem", position: 2, name: article.categoryName, item: `${SITE_ORIGIN}/category/${article.categorySlug}` },
          { "@type": "ListItem", position: 3, name: article.title, item: `${SITE_ORIGIN}/article/${article.id}` },
        ],
      },
    };
  }, [article]);

  useSeo({
    title: article ? `${article.title} | 网爆吃瓜实时楼` : "话题详情 | 吃瓜前线",
    description: article
      ? `${article.summary} 含完整时间线与来源说明，持续跟进网爆吃瓜实时楼更新。`
      : "吃瓜前线话题详情页，整理事件时间线与相关讨论。",
    keywords: article ? [...article.tags, "网爆吃瓜", "网爆吃瓜实时楼"].join(",") : "网爆吃瓜实时楼",
    path: `/article/${id}`,
    type: article ? "article" : "website",
    image: article?.cover,
    jsonLd: jsonLd ?? undefined,
  });

  if (!article) {
    return (
      <div className="w-full px-4 py-20 md:px-8">
        <div className="mx-auto max-w-lg rounded-lg border border-background-200 bg-background-100 px-6 py-14 text-center">
          <i className="ri-file-unknow-line text-3xl text-foreground-400" aria-hidden="true"></i>
          <h1 className="mt-3 font-heading text-xl font-bold text-foreground-950">这个话题还没整理好</h1>
          <p className="mt-2 text-sm text-foreground-600">它可能已经归档，或者链接不正确。</p>
          <Link
            to="/rank"
            className="mt-5 inline-flex cursor-pointer items-center gap-1 rounded-md bg-primary-500 px-4 py-2 font-label text-sm text-background-50 transition-colors hover:bg-primary-600"
          >
            去看看热榜
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full px-4 py-8 md:px-8 md:py-10">
      <Breadcrumbs
        items={[
          { label: "首页", to: "/" },
          { label: article.categoryName, to: `/category/${article.categorySlug}` },
          { label: "话题详情" },
        ]}
      />

      <article className="grid grid-cols-1 gap-10 lg:grid-cols-12">
        <div className="lg:col-span-8">
          <header>
            <div className="flex flex-wrap items-center gap-2">
              <Link
                to={`/category/${article.categorySlug}`}
                className="cursor-pointer rounded-full bg-secondary-100 px-3 py-1 font-label text-xs text-secondary-800 transition-colors hover:bg-secondary-200"
              >
                {article.categoryName}
              </Link>
              {article.live && (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-500 px-3 py-1 font-label text-xs text-background-50">
                  <span className="h-1.5 w-1.5 animate-pulse-dot rounded-full bg-background-50" />
                  实时楼更新中
                </span>
              )}
            </div>

            <h1 className="mt-4 font-heading text-2xl font-black leading-snug tracking-tight text-foreground-950 md:text-4xl">
              {article.title}
            </h1>

            <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 font-label text-xs text-foreground-500 md:text-sm">
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-user-line" aria-hidden="true"></i>
                {article.author}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-time-line" aria-hidden="true"></i>
                {article.publishedAt}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-book-open-line" aria-hidden="true"></i>
                {article.readMinutes} 分钟阅读
              </span>
              <span className="inline-flex items-center gap-1.5 text-primary-700">
                <i className="ri-fire-line" aria-hidden="true"></i>
                {formatHeat(article.heat)} 热度
              </span>
            </div>
          </header>

          <div className="mt-6 aspect-[16/9] w-full overflow-hidden rounded-lg bg-background-100">
            <img
              src={article.cover}
              alt={`${article.title} 话题配图`}
              title={`${article.title} 相关内容`}
              className="h-full w-full object-cover object-top"
            />
          </div>

          <div className="mt-7 rounded-lg border border-accent-200 bg-accent-50 p-5">
            <h2 className="flex items-center gap-2 font-heading text-base font-bold text-accent-900">
              <i className="ri-lightbulb-flash-line" aria-hidden="true"></i>
              核心速览
            </h2>
            <p className="mt-2 text-sm leading-relaxed text-foreground-700">{article.takeaway}</p>
          </div>

          <div className="mt-8 flex flex-col gap-5">
            {article.body.map((paragraph, index) => (
              <p key={index} className="text-base leading-loose text-foreground-700">
                {paragraph}
              </p>
            ))}
          </div>

          <section className="mt-10 rounded-lg border border-background-200 bg-background-100 p-5 md:p-6">
            <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
              <i className="ri-git-branch-line text-primary-600" aria-hidden="true"></i>
              时间线
            </h2>
            <ol className="mt-5 flex flex-col">
              {article.timeline.map((item, index) => (
                <li key={`${item.time}-${index}`} className="relative flex gap-4 pb-6 last:pb-0">
                  <div className="flex flex-col items-center">
                    <span className="mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full bg-primary-500" />
                    {index !== article.timeline.length - 1 && <span className="mt-1 w-px flex-1 bg-background-300" />}
                  </div>
                  <div className="min-w-0 pb-1">
                    <span className="rounded-md bg-background-50 px-2 py-0.5 font-label text-xs font-medium text-primary-700">
                      {item.time}
                    </span>
                    <p className="mt-2 text-sm leading-relaxed text-foreground-700 md:text-base">{item.text}</p>
                  </div>
                </li>
              ))}
            </ol>
          </section>

          <div className="mt-6 flex flex-wrap items-center gap-2">
            <span className="font-label text-xs text-foreground-500">话题标签</span>
            {article.tags.map((tag) => (
              <span key={tag} className="rounded-full bg-background-100 px-3 py-1 font-label text-xs text-foreground-700">
                #{tag}
              </span>
            ))}
          </div>

          <section className="mt-12">
            <h2 className="font-heading text-lg font-bold text-foreground-950 md:text-xl">相关推荐</h2>
            <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2">
              {related.map((item) => (
                <ArticleCard key={item.id} article={item} />
              ))}
            </div>
          </section>
        </div>

        <aside className="lg:col-span-4">
          <div className="lg:sticky lg:top-24 flex flex-col gap-6">
            <section className="rounded-lg border border-background-200 bg-background-100 p-5">
              <h2 className="font-heading text-base font-bold text-foreground-950">内容说明</h2>
              <ul className="mt-4 flex flex-col gap-3 text-sm leading-relaxed text-foreground-600">
                <li className="flex gap-2">
                  <i className="ri-checkbox-circle-line mt-0.5 text-primary-600" aria-hidden="true"></i>
                  内容为公开讨论整理，不代表对任何一方的定性。
                </li>
                <li className="flex gap-2">
                  <i className="ri-checkbox-circle-line mt-0.5 text-primary-600" aria-hidden="true"></i>
                  时间线按公开渠道发布时间排序，来源不清的会特别标注。
                </li>
                <li className="flex gap-2">
                  <i className="ri-checkbox-circle-line mt-0.5 text-primary-600" aria-hidden="true"></i>
                  涉及个人隐私的信息不会出现在整理内容中。
                </li>
              </ul>
            </section>

            <section className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h2 className="font-heading text-base font-bold text-foreground-950">实时热度前五</h2>
              <ol className="mt-4 flex flex-col gap-3">
                {hotList.map((item, index) => (
                  <li key={item.id}>
                    <Link to={`/article/${item.id}`} className="group flex cursor-pointer items-start gap-3">
                      <span className="font-heading text-sm font-bold text-primary-500">{index + 1}</span>
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm leading-snug text-foreground-800 transition-colors group-hover:text-primary-700">
                          {item.title}
                        </span>
                        <span className="mt-1 block font-label text-xs text-foreground-500">
                          {formatHeat(item.heat)} 热度
                        </span>
                      </span>
                    </Link>
                  </li>
                ))}
              </ol>
              <Link
                to="/rank"
                className="mt-4 inline-flex cursor-pointer items-center gap-1 font-label text-sm font-medium text-primary-700 transition-colors hover:text-primary-800"
              >
                查看完整榜单
                <i className="ri-arrow-right-line" aria-hidden="true"></i>
              </Link>
            </section>
          </div>
        </aside>
      </article>
    </div>
  );
}